export interface INewsTocItem {
	id: string
	label: string
	level: 2 | 3
	children: INewsTocItem[]
}

const HTML_ENTITIES: Record<string, string> = {
	amp: '&',
	lt: '<',
	gt: '>',
	quot: '"',
	apos: "'",
	nbsp: ' ',
	ndash: '–',
	mdash: '—',
	hellip: '…',
	bdquo: '„',
	rdquo: '”',
	ldquo: '“',
}

const decodeEntities = (text: string) =>
	text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (entity, code: string) => {
		if (code[0] === '#') {
			const charCode = code[1]?.toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10)
			return Number.isNaN(charCode) ? entity : String.fromCodePoint(charCode)
		}
		return HTML_ENTITIES[code.toLowerCase()] ?? entity
	})

const slugify = (text: string) =>
	text
		.toLowerCase()
		.replace(/ł/g, 'l')
		.normalize('NFD')
		.replace(/[\u0300-\u036F]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')

export const buildNewsToc = (html: string) => {
	const items: INewsTocItem[] = []
	const usedIds = new Set<string>()

	const content = html.replace(/<h([23])(\s[^>]*)?>([\s\S]*?)<\/h\1>/gi, (heading, level: string, attrs = '', inner: string) => {
		const label = decodeEntities(inner.replace(/<[^>]*>/g, ''))
			.replace(/\s+/g, ' ')
			.trim()
		if (!label) return heading

		const existingId = attrs.match(/\sid\s*=\s*["']([^"']+)["']/i)?.[1]
		let id = existingId ?? (slugify(label) || 'section')

		if (!existingId) {
			const baseId = id
			let suffix = 2
			while (usedIds.has(id)) id = `${baseId}-${suffix++}`
		}
		usedIds.add(id)

		const item: INewsTocItem = { id, label, level: level === '2' ? 2 : 3, children: [] }
		const parent = items[items.length - 1]

		if (item.level === 3 && parent) parent.children.push(item)
		else items.push(item)

		return existingId ? heading : `<h${level} id="${id}"${attrs}>${inner}</h${level}>`
	})

	return { content, items }
}
